import React, { useState } from 'react';
import PartsList from './PartsList';
import { Download } from 'lucide-react';
import { Button } from '../ui/button';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, TabStopType, TabStopPosition } from 'docx';

interface PartsListDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  searchTerm?: string;
  matchesWildcard?: (text: string, pattern: string) => boolean;
  showToggle?: boolean;
  onAddToInput?: (text: string) => void;
  onCopyResult?: () => void;
  showToast?: (message: string) => void;
  forceSimpleList?: boolean;
  showHeader?: boolean;
  onSearchDisabled?: (disabled: boolean) => void;
}

const PartsListDisplay: React.FC<PartsListDisplayProps> = ({
  result,
  theme = 'light',
  searchTerm = '',
  matchesWildcard,
  showToggle = false,
  onAddToInput,
  onCopyResult,
  showToast,
  forceSimpleList = false,
  showHeader = true,
  onSearchDisabled
}) => {
  const [showStructured, setShowStructured] = useState(false)
  const [showSimpleList, setShowSimpleList] = useState(forceSimpleList)

  // Notify parent when search should be disabled
  React.useEffect(() => {
    if (onSearchDisabled) {
      onSearchDisabled(showStructured)
    }
  }, [showStructured, onSearchDisabled])

  // Check if the result looks like a parts list object
  const isPartsListResult = (data) => {
    if (!data || typeof data !== 'object') return false
    
    // Check if it has the expected structure
    const keys = Object.keys(data)
    if (keys.length === 0) return false
    
    // Check if most entries have parts list structure
    let validEntries = 0
    keys.forEach(key => {
      const entry = data[key]
      if (entry && typeof entry === 'object' && (
          entry.Description || entry.description ||
          entry.Details || entry.details ||
          entry.Citations || entry.citations || 
          entry.Reference_Numerals || entry.reference_numerals ||
          (entry.citations && Array.isArray(entry.citations)) ||
          (entry.reference_numerals && Array.isArray(entry.reference_numerals))
      )) {
        validEntries++
      }
    })
    
    // If at least 50% of entries match the pattern, consider it a parts list
    return validEntries / keys.length >= 0.5
  }

  const exportToDocx = async () => {
    try {
      // Extract reference numerals and create simple list
      const partsList = [];
      // Filter out "Unknown Components" entries
      const filteredParts = Object.keys(result).reduce((acc, key) => {
        const keyLower = key.toLowerCase().replace(/_/g, ' ');
        if (!keyLower.includes('unknown component')) {
          acc[key] = result[key];
        }
        return acc;
      }, {});

      Object.entries(filteredParts).forEach(([key, part]: [string, any]) => {
        const numerals = part.Reference_Numerals || part.reference_numerals;
        if (numerals) {
          const numeralArray = Array.isArray(numerals) ? numerals : [numerals];
          numeralArray.forEach(numeral => {
            partsList.push({ numeral: numeral.toString(), name: key });
          });
        } else {
          // If no numerals, just use the key as the part name
          partsList.push({ numeral: '', name: key });
        }
      });

      // Sort by numeral if numeric, handling ranges like "141 to 146"
      partsList.sort((a, b) => {
        const getNumericValue = (str) => {
          if (!str) return NaN

          // Handle ranges like "141 to 146" by taking the first number
          if (str.includes(' to ')) {
            const firstNumber = str.split(' to ')[0].trim()
            return parseInt(firstNumber, 10)
          }

          // Handle ranges like "141-146" by taking the first number
          if (str.includes('-') && /^\d+\s*-\s*\d+$/.test(str)) {
            const firstNumber = str.split('-')[0].trim()
            return parseInt(firstNumber, 10)
          }

          // Extract the first number from the string
          const match = str.match(/\d+/)
          return match ? parseInt(match[0], 10) : NaN
        }

        const aNum = getNumericValue(a.numeral);
        const bNum = getNumericValue(b.numeral);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return aNum - bNum;
        }
        return a.numeral.localeCompare(b.numeral);
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            // Title
            new Paragraph({
              text: "PARTS LIST",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 400,
              },
            }),
            // Parts list items
            ...partsList.map(item => {
              const cleanName = item.name.replace(/_/g, ' ');

              if (item.numeral) {
                return new Paragraph({
                  children: [
                    new TextRun({
                      text: item.numeral,
                      bold: true,
                    }),
                    new TextRun({
                      text: `\t${cleanName}`,
                    }),
                  ],
                  tabStops: [
                    {
                      type: TabStopType.LEFT,
                      position: 1000,
                    },
                  ],
                  spacing: {
                    after: 100,
                  },
                });
              } else {
                return new Paragraph({
                  text: cleanName,
                  spacing: {
                    after: 100,
                  },
                });
              }
            }),
          ],
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'parts-list.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Parts list exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting parts list to DOCX:', error);
      if (showToast) {
        showToast('Error exporting parts list');
      }
    }
  }

  if (!isPartsListResult(result)) {
    return null // Not a parts list, don't render this component
  }

  // Filter out "Unknown Components" entries
  const filteredResult = Object.keys(result).reduce((acc, key) => {
    // Skip entries where the key includes "unknown" and "component" (handles both underscore and space variations)
    const keyLower = key.toLowerCase().replace(/_/g, ' ');
    if (keyLower.includes('unknown component')) {
      return acc;
    }
    acc[key] = result[key];
    return acc;
  }, {});

  // If no entries remain after filtering, don't render
  if (Object.keys(filteredResult).length === 0) {
    return null;
  }

  return (
    <div className={showHeader ? "space-y-3" : ""}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className={`text-lg font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            📄 Parts List
          </div>
          {showToggle && (
            <div className="flex items-center gap-2">
              {onAddToInput && (
                <button
                  onClick={() => {
                    onAddToInput(typeof filteredResult === 'object' ? JSON.stringify(filteredResult, null, 2) : String(filteredResult));
                    if (showToast) showToast('Result added to chat input!');
                  }}
                  className={`text-xs py-1 px-3 rounded-md transition-colors ${
                    theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  Add to Chat
                </button>
              )}
              <button
                onClick={exportToDocx}
                className={`text-xs py-1 px-3 rounded-md transition-colors flex items-center gap-1 ${
                  theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title="Export parts list as Word document"
              >
                <Download className="w-3 h-3" />
                Export DOCX
              </button>
              <button
                onClick={() => {
                  if (onCopyResult) onCopyResult();
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                }`}
              >
                Copy
              </button>
              <button
                onClick={() => {
                  setShowStructured(!showStructured)
                  if (showSimpleList) setShowSimpleList(false)
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showStructured 
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-blue-600 text-white hover:bg-blue-500' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}
              >
                {showStructured ? 'Show Formatted' : 'Show Raw Data'}
              </button>
              <button
                onClick={() => {
                  setShowSimpleList(!showSimpleList)
                  if (showStructured) setShowStructured(false)
                }}
                className={`text-xs py-1 px-3 rounded-md transition-colors ${
                  showSimpleList
                    ? theme === 'dark' ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                    : theme === 'dark' ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-green-500 text-white hover:bg-green-600'
                }`}
              >
                {showSimpleList ? 'Show Detailed' : 'View Simple List'}
              </button>
            </div>
          )}
        </div>
      )}
      
      {showStructured ? (
        <div className={`p-3 rounded-md ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className={`text-xs mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Structured Data Result
          </div>
          <pre className="text-sm whitespace-pre-wrap font-mono  overflow-y-auto">
            {JSON.stringify(filteredResult, null, 2)}
          </pre>
        </div>
      ) : (
        <div className={`rounded-md ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'} border ${theme === 'dark' ? 'border-gray-600' : 'border-gray-200'}`}>
          <PartsList
            parts={filteredResult}
            theme={theme}
            showToggle={false}
            searchTerm={searchTerm}
            matchesWildcard={matchesWildcard}
            forceSimpleList={forceSimpleList || showSimpleList}
          />
        </div>
      )}
    </div>
  )
}

export default PartsListDisplay